import { TableClient } from "@azure/data-tables";
import type { TokenCredential } from "@azure/identity";
import {
  type ApplyRequest, entityToApplyRequest, applyRequestToEntity,
  APPLY_REQUEST_PK, APPLY_REQUEST_RK, isInFlight, MAX_LEASE_TAKEOVERS,
} from "@m8t-stack/platform-release";
import { discoverStampStorage } from "./platform-storage-discovery.js";

/**
 * How many times an abandoned in-flight row may be taken over before the
 * updater stops rescuing it and holds. Each takeover costs a full converge, and
 * only happens after a 30-minute lease expiry — but the job ticks every 5
 * minutes, so without a cap a converge that dies mid-apply every time would
 * re-roll real infra forever, unattended. Two rescues (three attempts total)
 * mirrors the health gate's own breaker budget: one retry, then stop.
 *
 * Re-exported from platform-release rather than owned here: the gateway's reaper
 * derives its own patience from this number — it must never settle a row this
 * job is still legitimately retrying — and two processes reading two copies of
 * it is exactly how that guarantee rots without anything failing.
 */
export { MAX_LEASE_TAKEOVERS };

export async function openApplyTable(opts: { credential: TokenCredential; subscriptionId: string; resourceGroup: string }): Promise<TableClient> {
  const { tableEndpoint } = await discoverStampStorage(opts);
  return new TableClient(tableEndpoint, "Metadata", opts.credential);
}

interface Row { value: string; etag: string }
interface EntityReader {
  getEntity(partitionKey: string, rowKey: string): Promise<unknown>;
}

function hasStatus(e: unknown, n: number): boolean {
  return (e as { statusCode?: number }).statusCode === n;
}

/** A 403 from the Metadata table: the caller's identity holds no table data role on the stamp account. */
export function isTableAccessDenied(e: unknown): boolean {
  return hasStatus(e, 403);
}

/**
 * Read the apply-request row. Swallows ONLY a 404 (row absent) → `null`;
 * anything else (esp. 403 — e.g. a Managed Identity missing "Storage Table
 * Data Contributor") rethrows, so an access failure surfaces loudly instead
 * of silently reading as "no request pending".
 */
export async function readApplyRequest(client: EntityReader): Promise<(ApplyRequest & { etag: string }) | null> {
  try {
    const row = (await client.getEntity(APPLY_REQUEST_PK, APPLY_REQUEST_RK)) as Row;
    return { ...entityToApplyRequest(row), etag: row.etag };
  } catch (e) {
    if (hasStatus(e, 404)) return null;
    throw e;
  }
}

/**
 * pending → claimed, ETag-guarded. Returns the claimed row, or null if not
 * claimable / lost the race.
 *
 * Also reclaims an ABANDONED IN-FLIGHT row once its lease has expired: if a
 * prior execution died (hard process kill — no time to run any catch) before
 * writing a terminal status, the row would otherwise sit in-flight forever,
 * since nothing else ever reads `leaseUntil`. That applies to `applying` just
 * as much as to `claimed` — and far more often, since `applying` spans the
 * whole converge while `claimed` is a window of milliseconds. Both are
 * `isInFlight`, both hold a lease, so both are rescuable on expiry.
 * A row is claimable when it is `pending`, it is `awaiting-engine-update`
 * (the self-update hop parked it there deliberately so the NEXT tick — now
 * running the updated engine — re-claims and proceeds; see converge.ts step 4.
 * That next tick runs the target release's installer image, whose baked
 * installer release EQUALS the target (and whose baked CLI_VERSION is ≥ that
 * release's cli.min) by construction, so `needsEngineUpdate` returns false and
 * it proceeds to apply — no infinite hop), OR it is in-flight AND its
 * `leaseUntil` is strictly in the past
 * relative to `nowIso` (both plain ISO strings — no reliance on the machine
 * clock, just the two stamps already on the wire). An in-flight row with a
 * still-live lease is left alone: a healthy execution owns it.
 *
 * A takeover bumps `attempt`. Once the row has already been rescued
 * `MAX_LEASE_TAKEOVERS` times it is NOT claimed again: it goes terminal as
 * `held` (breaker `held`) so the 5-minute cron stops re-rolling infra behind a
 * converge that cannot finish, and the founder-facing surfaces — which render
 * `held` as an actionable failure, and treat terminal rows as overwritable by a
 * fresh press — get their retry door back.
 */
export async function claimApplyRequest(client: TableClient, executionId: string, nowIso: string, leaseMs: number): Promise<ApplyRequest | null> {
  const cur = await readApplyRequest(client);
  if (!cur) return null;
  const leaseExpired = isInFlight(cur.status) && cur.leaseUntil !== null && Date.parse(cur.leaseUntil) < Date.parse(nowIso);
  const claimable = cur.status === "pending" || cur.status === "awaiting-engine-update" || leaseExpired;
  if (!claimable) return null;
  const nowMs = Date.parse(nowIso);
  const leaseUntil = new Date((Number.isNaN(nowMs) ? Date.now() : nowMs) + leaseMs).toISOString();
  // Rescue budget is spent → stop rescuing and land a terminal status, rather
  // than claiming a row this updater has already failed to carry twice.
  if (leaseExpired && cur.attempt >= MAX_LEASE_TAKEOVERS) {
    const held: ApplyRequest = {
      ...cur,
      status: "held",
      breaker: "held",
      result: {
        appliedVersion: cur.result?.appliedVersion ?? null,
        error:
          `the converge was abandoned mid-apply ${(cur.attempt + 1).toString()} times (last owner ${cur.claimedBy ?? "(unknown)"}, ` +
          `phase "${cur.phase ?? "(none)"}"); the updater stopped retrying to avoid re-rolling infra behind a converge that cannot finish`,
      },
      updatedAt: nowIso,
    };
    try {
      await client.updateEntity({ ...applyRequestToEntity(held), etag: cur.etag } as never, "Merge", { etag: cur.etag });
    } catch (e) {
      if (!hasStatus(e, 412)) throw e;
    }
    return null;
  }
  const claimed: ApplyRequest = {
    ...cur,
    status: "claimed",
    claimedBy: executionId,
    leaseUntil,
    ...(leaseExpired ? { attempt: cur.attempt + 1 } : {}),
    updatedAt: nowIso,
  };
  try {
    await client.updateEntity({ ...applyRequestToEntity(claimed), etag: cur.etag } as never, "Merge", { etag: cur.etag });
    return claimed;
  } catch (e) {
    // 412 precondition failed → another execution claimed it first; anything
    // else (esp. 403) is a real failure and must not read as "lost the race".
    if (hasStatus(e, 412)) return null;
    throw e;
  }
}

/**
 * ETag read-modify-write for a status/phase transition. `readApplyRequest`
 * only returns null on a 404, so the early-return here is a genuine
 * "no request row" no-op. The `updateEntity` write is deliberately
 * unguarded: the job is the sole writer post-claim (parallelism 1), so a
 * failure here is a real bug that should surface loudly, not be swallowed.
 */
export async function patchApplyRequest(client: TableClient, mutate: (r: ApplyRequest) => ApplyRequest): Promise<void> {
  const cur = await readApplyRequest(client);
  if (!cur) return;
  const next = mutate(cur);
  await client.updateEntity({ ...applyRequestToEntity(next), etag: cur.etag } as never, "Merge", { etag: cur.etag });
}
